import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { DSData, Service } from 'webapp-types';
import { glueDimensionValues } from 'webapp/utils/common';
import { hashCode } from 'webapp/utils/number';

import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataSelectEditDataPreviewCompareComponent from 'webapp/components/data-select/edit/data-preview-compare';

import type DataSelect from 'webapp/models-custom/data-select';

import type { RSjqXHR, RSPromise } from 'webapp/services/request';

export interface PreviewCompare {
	valueStr: string;
	changeStr: string;
	indicatorClass: string;
	date?: string;
}

interface PreviewRow {
	index: number;
	date: string;
	attribute: string;
	unit: string;
	valueStr: SafeString;
	compares: (PreviewCompare | null)[];
}

interface Args {
	model: DataSelect;
	onContentChange?: () => void;
}

export default class DataSelectEditDataPreviewComponent extends Component<Args> {
	@service('timer') declare timerService: Service.Timer;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;

	@tracked isPreviewOpen = true;
	@tracked previewPromise: RSPromise<unknown> | null = null;
	@tracked previewData: DSData.RootTop | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		next(() => {
			this.fetchData(true);
		});
	}

	get isPreviewLoading() {
		return !!this.previewPromise;
	}

	get showTable() {
		return this.isPreviewLoading || !!this.previewData;
	}

	get uniqueFetchDataHash() {
		return hashCode(JSON.stringify(this.args.model.toJSON()));
	}

	get compares() {
		return this.args.model.limitations.compareCMs.filter((c) => !c.isNone);
	}

	get compareCount() {
		return this.compares.length;
	}

	get showAttributes() {
		return this.previewRows.some((r) => !!r.attribute);
	}

	get showUnits() {
		return this.previewRows.some((r) => !!r.unit);
	}

	get colspan() {
		let colspan = 3; // number, date, value -- always
		if (this.showAttributes) {
			colspan++;
		}
		if (this.showUnits) {
			colspan++;
		}
		colspan += this.compareCount;
		return colspan;
	}

	@cached
	get points() {
		return this.mmDataSelectsService.getPointsFromData(this.args.model, this.previewData);
	}

	@cached
	get previewRows(): PreviewRow[] {
		return this.points.map((p, i) => {
			const row = {
				index: i + 1,
				date: p.timeLabelLong || p.timeLabel,
				attribute: glueDimensionValues(p.metricSelectionData?.dimensionValues),
				unit: p.metricSelectionData?.unit || '',
				valueStr: htmlSafe(p.valueStrFormatted),
				compares: new Array(this.compareCount).fill(null),
			};
			p.comparePoints.forEach((p) => {
				const compareData = {
					date: p.timeLabelLong || p.timeLabel,
					valueStr: p.valueStr,
					changeStr: p.changeStr,
					indicatorClass: p.indicatorClass,
				};
				row.compares[p.compareIndex] = compareData;
			});
			return row;
		});
	}

	@cached
	get isSingleCompare() {
		return this.compares.length === 1;
	}

	fetchData(isInitialCheck = false) {
		const dataSelect = this.args.model;

		if (!dataSelect.metricSetupKey) {
			return;
		}

		if (!isInitialCheck && this.previewPromise) {
			(this.previewPromise.jqPromise as RSjqXHR)?.abort();
		}

		const checkIsCurrentPromise = (promise: Promise<unknown>) => {
			return promise === this.previewPromise;
		};

		const promise = this.mmDataSelectsService.fetchData(dataSelect);

		this.previewPromise = promise;

		promise
			.then((reply) => {
				if (checkIsCurrentPromise(promise)) {
					this.previewData = reply;
				}
			})
			.catch(() => {
				if (checkIsCurrentPromise(promise)) {
					this.previewData = null;
				}
			})
			.finally(() => {
				if (checkIsCurrentPromise(promise)) {
					this.previewPromise = null;
					if (this.args.onContentChange) {
						schedule('afterRender', () => {
							if (this.args.onContentChange) {
								this.args.onContentChange();
							}
						});
					}
				}
			});
	}

	@action
	scheduleFetch() {
		debounce<DataSelectEditDataPreviewComponent, () => void>(this, this.fetchData, 150);
	}

	@action
	togglePreview() {
		this.isPreviewOpen = !this.isPreviewOpen;
	}
	<template>
		<div
			class="data-preview {{if this.isPreviewOpen 'open'}}"
			data-hash={{this.uniqueFetchDataHash}}
			{{didUpdate this.scheduleFetch this.uniqueFetchDataHash}}
		>

			<a role="button" class="toggler" {{on "click" this.togglePreview}}>
				<CommonUiIconComponent @name="ChevronDown" @size="sm" />Data preview
			</a>

			{{#if this.showTable}}
				<div class="ct">
					<table>
						<thead>
							<tr>
								<th>#</th>
								<th class="th--date">Date</th>
								{{#if this.showAttributes}}
									<th class="th--dim">Dimension</th>
								{{/if}}
								{{#if this.showUnits}}
									<th class="th--unit">Unit</th>
								{{/if}}
								<th class="th--value r">Value</th>
								{{#if this.isSingleCompare}}
									<th class="l"></th>
								{{/if}}
								{{#each this.compares as |compare|}}
									<th class={{if this.isSingleCompare "l" "r"}}>
										{{compare.titleUX}}
									</th>
								{{/each}}
							</tr>
						</thead>
						<tbody>
							{{#if this.isPreviewLoading}}
								<tr>
									<td class="loading" colspan={{this.colspan}}>Loading data...</td>
								</tr>
							{{else if this.previewRows}}
								{{#each this.previewRows as |row|}}
									<tr>
										<td>{{row.index}}</td>
										<td class="v">{{row.date}}</td>
										{{#if this.showAttributes}}
											<td class="v">{{row.attribute}}</td>
										{{/if}}
										{{#if this.showUnits}}
											<td class="v">{{row.unit}}</td>
										{{/if}}
										<td class="r v">{{{row.valueStr}}}</td>
										{{#each row.compares as |compare|}}
											<DataSelectEditDataPreviewCompareComponent
												@compare={{compare}}
												@isSingleCompare={{this.isSingleCompare}}
											/>
										{{/each}}
									</tr>
								{{/each}}
							{{else}}
								<tr>
									<td class="loading" colspan={{this.colspan}}>No data.</td>
								</tr>
							{{/if}}
						</tbody>
					</table>
				</div>
			{{else}}
				<div class="nodata">
					Nothing to display here. Please select a data source.
				</div>
			{{/if}}

		</div>
	</template>
}
